import { AlfredIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

import { ALFRED_THANKS_MS } from "../lib/limits.ts";
import { PLATFORM } from "../lib/platform.ts";

import { ariaKeys } from "../work/digitacion/Kbd.tsx";
import { fromMac, KEYS, keysLabel } from "../work/digitacion/keymap.ts";
import { ALFRED_TEXTS as TEXTS } from "./alfred.ts";
import { AlfredDialog } from "./AlfredDialog.tsx";

/**
 * «Cuéntale a Alfred» en la cabecera de Work: discreto, con su diálogo y el
 * «Listo» al lado mientras dura, porque aquí no hay barra de estado.
 * Digitación monta el diálogo por su cuenta, como un diálogo más de la
 * pantalla, y dice el «Listo» en su barra.
 *
 * Alt+B (⌃B en Mac) lo abre desde cualquier sitio de Work, el mismo atajo
 * que en Digitación. Con otro diálogo abierto no: el teclado es de aquel.
 */
export function AlfredButton() {
  const [open, setOpen] = useState(false);
  const [thanks, setThanks] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const press = PLATFORM === "mac" ? fromMac(event) : event;
      if (
        press === null ||
        !press.altKey ||
        press.ctrlKey ||
        press.shiftKey ||
        press.metaKey ||
        press.code !== "KeyB"
      ) {
        return;
      }
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        return;
      }
      event.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!thanks) return;
    const timer = window.setTimeout(() => {
      setThanks(false);
    }, ALFRED_THANKS_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [thanks]);

  return (
    <>
      <span role="status" className="truncate text-sm text-muted-foreground">
        {thanks ? TEXTS.thanks : ""}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 px-2 font-normal text-muted-foreground"
        aria-label={TEXTS.button}
        aria-keyshortcuts={ariaKeys(KEYS.alfred)}
        title={`${TEXTS.command} (${keysLabel(KEYS.alfred)})`}
        onClick={() => {
          setOpen(true);
        }}
      >
        <AlfredIcon aria-hidden />
        <span className="hidden lg:inline">{TEXTS.button}</span>
      </Button>
      <AlfredDialog
        open={open}
        onOpenChange={setOpen}
        onSent={() => {
          setThanks(true);
        }}
      />
    </>
  );
}
